<script setup lang="ts">
import { IconChevronDown } from '@tabler/icons-vue'
import Multiselect from '@vueform/multiselect'
import { type CountryCode, isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js/mobile'
import { ref, useTemplateRef } from 'vue'

import BaseIcon from '../../BaseIcon.vue'
import { useComputedAttrs, useComputedInput } from '../helpers'
import { PhoneNumberInputProps } from '../types'

import { CountryOption, formatPhoneNumber, getAllCountryOptions, parsePhoneNumber } from './helpers'

// TODO: make defaultCountry required and remove default value
const props = withDefaults(defineProps<PhoneNumberInputProps>(), {
  defaultCountry: 'US',
})
const initialValue = props.modelValue as string
const attrs = useComputedAttrs(props, 'tel')
const emit = defineEmits(['update:modelValue', 'change', 'blur'])
const input = useComputedInput(props, emit)

const countryOptions = getAllCountryOptions()
const getDefaultCountry = (): CountryCode => {
  const value = input.value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsedPhoneNumber = parsePhoneNumberFromString(value)

    // If the phone number is valid and has a country calling code, use that country
    if (parsedPhoneNumber?.country) {
      return parsedPhoneNumber.country
    }
  }

  return props.defaultCountry
}

const countryCode = ref<CountryCode>(getDefaultCountry())
const countryMultiselectRef = useTemplateRef<InstanceType<typeof Multiselect>>('countryMultiselectElement')

const phoneNumberFormattedValue = ref(formatPhoneNumber((input.value as string) ?? '', countryCode.value))
const phoneNumberInputRef = useTemplateRef<HTMLInputElement>('phoneNumberInputElement')

const handleCountrySelect = () => {
  phoneNumberInputRef.value?.setCustomValidity('')

  const parsedNumber = parsePhoneNumber(phoneNumberFormattedValue.value, countryCode, props.defaultCountry)
  input.value = parsedNumber
  phoneNumberFormattedValue.value = formatPhoneNumber(phoneNumberFormattedValue.value, countryCode.value)

  // Any validity errors have been cleared, but if it's still invalid, the error will show again on phone number input blur
  phoneNumberInputRef.value?.focus()
}

// Allow reselection of currently selected country
const handleCountryReselect = (_: any, option: CountryOption) => {
  countryMultiselectRef.value?.select(option)
}

/** aria-placeholder and aria-multiselectable are not supported on role="combobox"; clear them via library aria prop. */
const countryMultiselectAria = { 'aria-placeholder': undefined, 'aria-multiselectable': undefined }

const handlePhoneNumberInputChange = (event: Event) => {
  phoneNumberInputRef.value?.setCustomValidity('')
  emit('change')

  const number = (event.target as HTMLInputElement).value
  const parsedNumber = parsePhoneNumber(number, countryCode, props.defaultCountry)
  input.value = parsedNumber
  phoneNumberFormattedValue.value = formatPhoneNumber(number, countryCode.value)
}

const handlePhoneNumberInputBlur = () => {
  const value = input.value
  const hasValue = typeof value === 'string' && value.trim() !== ''
  if (hasValue && !isValidPhoneNumber(value) && value !== initialValue) {
    phoneNumberInputRef.value?.setCustomValidity('Please enter a valid mobile phone number.')
  } else {
    phoneNumberInputRef.value?.setCustomValidity('')
  }
  emit('blur')
}
</script>

<template>
  <div class="phone-number-field">
    <Multiselect
      ref="countryMultiselectElement"
      v-model="countryCode"
      @open="countryMultiselectRef?.focus()"
      @select="handleCountrySelect"
      @deselect="handleCountryReselect"
      :options="countryOptions"
      :disabled="props.disabled || props.readOnly"
      :searchable="true"
      :track-by="['countryCode', 'callingCode', 'label']"
      :can-clear="false"
      value-prop="countryCode"
      class="multi-select"
      :aria="countryMultiselectAria"
    >
      <template #caret="{ handleCaretClick, isOpen }">
        <span
          class="multiselect-caret-icon"
          :class="{ 'is-open': isOpen }"
          :style="{ pointerEvents: isOpen ? 'auto' : 'none' }"
          @click="handleCaretClick"
        >
          <BaseIcon class="multiselect-caret-base-icon" :icon="IconChevronDown" />
        </span>
      </template>
      <template #singlelabel="{ value: option }">
        <div class="selected-country">
          <span :class="`flag-icon fi fi-${option.countryCode.toLowerCase()}`"></span>
          <span class="selected-country-code">{{ option?.callingCode }}</span>
        </div>
      </template>
      <template #option="{ option }">
        <span :class="`flag-icon fi fi-${option.countryCode.toLowerCase()}`"></span> {{ option.label }}
        {{ option.callingCode }}
      </template>
    </Multiselect>
    <input
      type="tel"
      class="input"
      :value="phoneNumberFormattedValue"
      @input="handlePhoneNumberInputChange"
      @blur="handlePhoneNumberInputBlur"
      ref="phoneNumberInputElement"
      v-bind="attrs"
      v-autofocus="props.autofocus"
      :aria-required="props.required"
      :aria-invalid="props.invalid"
      :tabindex="props.tabindex"
    />
  </div>
</template>

<style scoped>
.phone-number-field {
  display: flex;
  gap: 8px;
  width: 100%;
}

.multi-select {
  width: 200px;

  .multiselect-caret-base-icon {
    font-size: 14.667px;
  }

  .multiselect-search:focus ~ .selected-country {
    display: none;
  }

  > :deep(.multiselect-dropdown) {
    width: 200px;
    left: 0;
    border-top-right-radius: var(--radius-tiny);
  }
}

.flag-icon {
  flex-shrink: 0;
  margin-right: 4px;
}

.selected-country {
  display: flex;
  flex-grow: 1;
  padding: 0 12px;

  .selected-country-code {
    position: relative;
    margin-left: 0.3rem;
  }
}
</style>
